import { createContext, useContext, useEffect, useState } from "react";
import { login as apiLogin, logout as apiLogout, fetchMe } from "@/lib/api";

const TOKEN_KEY = "cot_token";
const USER_KEY = "cot_user";
const AuthContext = createContext(null);

function readCachedUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  useEffect(() => {
    // No stored token → not logged in (avoids a needless /me 401 on the login page).
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(false);
      return;
    }
    fetchMe()
      .then((u) => {
        localStorage.setItem(USER_KEY, JSON.stringify(u));
        setUser(u);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          // Genuine logout (the interceptor also clears the token + redirects).
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(false);
        } else {
          // Transient (timeout / network / backend busy): the token is still valid,
          // so keep the session going with the last known user instead of kicking
          // the user back to the login page.
          const cached = readCachedUser();
          setUser(cached || { email: "", name: "", role: "user" });
        }
      });
  }, []);

  const doLogin = async (email, password) => {
    const data = await apiLogin(email, password);
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    await apiLogout();
    setUser(false);
    window.location.href = "/login";
  };

  const updateSession = (data) => {
    if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
    if (data?.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
    }
  };

  return (
    <AuthContext.Provider value={{ user, doLogin, logout, updateSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
